import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Modal, Select, useToast } from '@bbpos/ui';
import { getSubunitMultiplier } from '@bbpos/utils';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatDocAmount, docCurrencySymbol } from '@/utils/purchase-currency';
import { useVendorBills } from '@/features/vendor-bills';
import { useDebitNotes, usePurchaseReturns, useApplyDebitNote, useRefundDebitNote } from './use-purchase-returns-query';
import { DEBIT_NOTE_LABEL, DEBIT_NOTE_TONE } from './PoReturnsCard';
import type { DebitNoteListItem, DebitNoteRefundMethod } from './types';

const REFUND_METHODS: { value: DebitNoteRefundMethod; label: string }[] = [
  { value: 'BANK_TRANSFER', label: 'Bank transfer' }, { value: 'UPI', label: 'UPI' },
  { value: 'CHEQUE', label: 'Cheque' }, { value: 'CARD', label: 'Card' },
];

function fmtDate(v: string | null | undefined) {
  return v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

/**
 * Supplier page: goods sent back and the debit notes they raised (QA PU3-005). An OWNER sets
 * open credit off against another open bill of the supplier, or records it refunded.
 */
export function SupplierReturnsPanel({ supplierId, isOwner }: { supplierId: string; isOwner: boolean }) {
  const { toast } = useToast();
  const tenantCurrency = useTenantCurrency();
  const { data: notes = [] } = useDebitNotes({ supplier_id: supplierId });
  const { data: returns = [] } = usePurchaseReturns({ supplier_id: supplierId });
  const { data: bills } = useVendorBills({ supplier_id: supplierId, page: 1, limit: 100 });
  const applyMutation = useApplyDebitNote();
  const refundMutation = useRefundDebitNote();

  const [applyNote, setApplyNote] = useState<DebitNoteListItem | null>(null);
  const [applyBillId, setApplyBillId] = useState('');
  const [refundNote, setRefundNote] = useState<DebitNoteListItem | null>(null);
  const [refundMethod, setRefundMethod] = useState<DebitNoteRefundMethod>('BANK_TRANSFER');
  const [refundRef, setRefundRef] = useState('');
  const [refundBase, setRefundBase] = useState('');

  const openBills = (note: DebitNoteListItem | null) =>
    (bills?.data ?? []).filter((b) => (b.status === 'OPEN' || b.status === 'PARTIALLY_PAID') && b.balance_due > 0
      && (b.currency_code ?? null) === (note?.currency_code ?? null));
  const cur = (n: { currency_code: string | null }) => n.currency_code ?? tenantCurrency;

  // The modals sit outside the blurred card: backdrop-filter would make it their containing block.
  return (
    <>
    <div className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm rounded-xl shadow-md hover:shadow-lg transition-all duration-200 animate-fade-in">
      <div className="px-6 py-4 border-b border-neutral-100 dark:border-neutral-800 bg-gradient-to-r from-violet-50 to-pink-50 dark:from-violet-900/20 dark:to-pink-900/20 rounded-t-xl">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Returns &amp; debit notes</h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Goods sent back to this supplier, and the credit they owe for them.</p>
      </div>
      {notes.length === 0 && returns.length === 0 ? (
        <p className="px-6 py-6 text-sm text-neutral-500 dark:text-neutral-400">No goods have been sent back to this supplier. Return goods from a receipt on the purchase order.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-800/60 text-xs uppercase text-neutral-500 dark:text-neutral-400">
              <tr>
                <th className="px-4 py-2 text-left">Debit note</th>
                <th className="px-4 py-2 text-left">Return</th>
                <th className="px-4 py-2 text-left">PO / bill</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2 text-right">Credit left</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {notes.map((n) => (
                <tr key={n.id} className="border-t border-neutral-100 dark:border-neutral-800 even:bg-neutral-50 dark:even:bg-neutral-900/40 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-all duration-200 animate-slide-up">
                  <td className="px-4 py-2 font-mono font-semibold text-neutral-900 dark:text-white">{n.number}<span className="block text-xs font-sans font-normal text-neutral-500">{fmtDate(n.created_at)}</span></td>
                  <td className="px-4 py-2 font-mono text-neutral-700 dark:text-neutral-300">{n.return_number}</td>
                  <td className="px-4 py-2">
                    <Link className="text-brand-600 hover:underline" to={`/inventory/purchase-orders/${n.purchase_order_id}`}>{n.po_number}</Link>
                    {n.bill_number && <> · <Link className="text-brand-600 hover:underline" to={`/vendor-bills/${n.vendor_bill_id}`}>{n.bill_number}</Link></>}
                    {n.applications.filter((a) => a.vendor_bill_id !== n.vendor_bill_id).map((a) => (
                      <span key={a.vendor_bill_id} className="block text-xs text-neutral-500">Set off on <Link className="text-brand-600 hover:underline" to={`/vendor-bills/${a.vendor_bill_id}`}>{a.bill_number}</Link> {formatDocAmount(a.amount, cur(n))}</span>
                    ))}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatDocAmount(n.total_amount, cur(n))}<span className="block text-xs text-neutral-500">incl. tax {formatDocAmount(n.tax_total, cur(n))}</span></td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold text-violet-700 dark:text-violet-300">{formatDocAmount(n.remaining, cur(n))}</td>
                  <td className="px-4 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${DEBIT_NOTE_TONE[n.status]}`}>{DEBIT_NOTE_LABEL[n.status]}</span></td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {isOwner && n.status === 'OPEN' && n.remaining > 0 && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="secondary" onClick={() => { setApplyNote(n); setApplyBillId(openBills(n)[0]?.id ?? ''); }}>Apply to bill</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setRefundNote(n); setRefundMethod('BANK_TRANSFER'); setRefundRef(''); setRefundBase(''); }}>Refunded</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {returns.filter((r) => r.status === 'DRAFT').map((r) => (
                <tr key={r.id} className="border-t border-neutral-100 dark:border-neutral-800 even:bg-neutral-50 dark:even:bg-neutral-900/40">
                  <td className="px-4 py-2 text-neutral-500">—</td>
                  <td className="px-4 py-2 font-mono text-neutral-700 dark:text-neutral-300">{r.return_number}</td>
                  <td className="px-4 py-2"><Link className="text-brand-600 hover:underline" to={`/inventory/purchase-orders/${r.purchase_order_id}`}>{r.po_number}</Link></td>
                  <td className="px-4 py-2 text-right text-neutral-500" colSpan={2}>{r.items.map((i) => `${i.item_name} × ${i.quantity}`).join(' · ')}</td>
                  <td className="px-4 py-2"><span className="rounded-full px-2 py-0.5 text-xs font-medium bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300">Draft — owner to post</span></td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

      <Modal open={applyNote != null} onClose={() => setApplyNote(null)} title={`Apply ${applyNote?.number ?? ''} to a bill`}
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setApplyNote(null)}>Keep</Button>
          <Button variant="primary" disabled={!applyBillId} loading={applyMutation.isPending}
            onClick={() => applyNote && applyMutation.mutate({ id: applyNote.id, vendorBillId: applyBillId }, {
              onSuccess: (d) => { setApplyNote(null); toast({ title: `${d.number} set off — ${formatDocAmount(d.remaining, cur(d))} credit left`, variant: 'success' }); },
              onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
            })}>Apply credit</Button></div>}>
        {applyNote && (openBills(applyNote).length === 0 ? (
          <p className="text-sm text-neutral-600 dark:text-neutral-300">This supplier has no open bill in {cur(applyNote)}. The credit stays open for the next bill.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              Up to {formatDocAmount(applyNote.remaining, cur(applyNote))} comes off the bill&apos;s balance. Anything the bill does not need stays as credit.
            </p>
            <Select value={applyBillId} onChange={(v) => setApplyBillId(typeof v === 'string' ? v : '')}
              options={openBills(applyNote).map((b) => ({ value: b.id, label: `${b.bill_number} — ${formatDocAmount(b.balance_due, cur(applyNote))} due` }))} />
          </div>
        ))}
      </Modal>

      <Modal open={refundNote != null} onClose={() => setRefundNote(null)} title={`Supplier refunded ${refundNote?.number ?? ''}`}
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setRefundNote(null)}>Keep</Button>
          <Button variant="primary" loading={refundMutation.isPending} disabled={!!refundNote?.currency_code && !(Number(refundBase) > 0)}
            onClick={() => refundNote && refundMutation.mutate({
              id: refundNote.id,
              payload: {
                method: refundMethod, ...(refundRef.trim() ? { reference: refundRef.trim() } : {}),
                ...(refundNote.currency_code ? { base_amount: Math.round(Number(refundBase) * getSubunitMultiplier(tenantCurrency)) } : {}),
              },
            }, {
              onSuccess: (d) => { setRefundNote(null); toast({ title: `${d.number} marked refunded`, variant: 'success' }); },
              onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
            })}>Record refund</Button></div>}>
        {refundNote && (
          <div className="space-y-3">
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              The supplier paid back the {formatDocAmount(refundNote.remaining, cur(refundNote))} credit. Cash refunds are not recorded here, because they would not reach cash closing.
            </p>
            <Select options={REFUND_METHODS} value={refundMethod} onChange={(v) => setRefundMethod((typeof v === 'string' ? v : 'BANK_TRANSFER') as DebitNoteRefundMethod)} />
            <Input value={refundRef} onChange={setRefundRef} placeholder="UTR / cheque no." />
            {refundNote.currency_code && (
              <div><label className="block text-sm font-medium mb-1">Received in {tenantCurrency} ({docCurrencySymbol(tenantCurrency)})</label><Input type="number" value={refundBase} onChange={setRefundBase} /></div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
